import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { NciIndicator } from "models/patient-chart/nci-indicator";
import { Observable } from "rxjs";
import { of } from "rxjs/observable/of";
import { catchError } from "rxjs/operators";
import { ConfigurationService } from "../configuration/configuration.service";

/**
 * TRD-201 / REQ-006 — clinical-ui consumer for the patient-charts indicators read endpoint.
 *
 * Endpoint contract (TRD §4.4):
 *   GET {apiUrl}/patient-charts/nci-indicators?orderId=<comma-separated>
 *   ->  200 OK + List<NciIndicator>
 *   Empty entries omitted; orders without a junction row are simply absent from the response.
 *   Tenant-scoped via JWT principal on the server (TRD §4.5).
 *
 * URL path (TDR-20): the `/patient-charts/` prefix lets a single service path-prefix map to a
 * single backing service on both EKS (Envoy Gateway HTTPRoute matches `/patient-charts/**` and
 * forwards verbatim — the `-strip-prefix-and-slash` filter only normalizes trailing slashes,
 * REPO-384) and ECS (API Gateway proxies `/patient-charts/{proxy+}` verbatim to the ECS service).
 * `apiUrl` here resolves to the bare service host via `ConfigurationService.resolvePatientChartsUrl()`
 * (either `config.patientChartsUrl` or `config.apiUrl`); the path suffix is appended here.
 *
 * Pattern intentionally mirrors NciAddendumsService:
 *   - Same apiUrl resolution (config.isLocal -> localEndpoints.patientCharts, else config.apiUrl)
 *   - HttpClient typed GET, default JSON parsing
 *   - No retry / no caching in v1 (TRD §4.4: "Caching: none in v1.")
 *
 * Failure mode: indicator data is best-effort UX. REQ-006 specifies what the icon means but
 * does not specify behavior on indicator-fetch failure. Fail-open with an empty array so a
 * transient backend error does not block the orders grid from rendering — the icon simply
 * won't appear until the next successful fetch. The error is logged so it remains observable
 * via the browser console / Datadog RUM. This matches the spirit of the existing
 * `OrderErrorResponse.handleError` toasts on the orders page (errors surface there; the
 * indicator is a passive decoration, not a primary action).
 */
@Injectable()
export class NciIndicatorsService {
  private readonly apiUrl: string;

  constructor(private http: HttpClient, private config: ConfigurationService) {
    this.apiUrl = this.config.resolvePatientChartsUrl();
  }

  /**
   * Fetches NCI indicators for the given order IDs.
   *
   * Empty input short-circuits to `Observable.of([])` to avoid a wasted HTTP round-trip
   * when the orders grid is empty (e.g. filters returned zero hits). The server also
   * short-circuits on an empty/missing `orderId` param (see NciIndicatorsController), but
   * skipping the call here saves the role-guard round-trip too.
   *
   * On HTTP error, returns an empty array (fail-open — see class-level docstring).
   */
  getIndicators(orderIds: number[]): Observable<NciIndicator[]> {
    if (!orderIds || orderIds.length === 0) {
      return of<NciIndicator[]>([]);
    }
    const url = `${this.apiUrl}/patient-charts/nci-indicators?orderId=${orderIds.join(",")}`;
    return this.http.get<NciIndicator[]>(url).pipe(
      catchError((err) => {
        // Fail-open: log + return empty so orders grid still renders.
        // tslint:disable-next-line:no-console
        console.warn("NciIndicatorsService.getIndicators failed", err);
        return of<NciIndicator[]>([]);
      })
    );
  }
}



// WEBPACK FOOTER //
// ./src/providers/nci-indicators/nci-indicators-service.ts